import {
  computeSloColors,
  computeWasteColors,
  type HeatmapViewMode,
} from '../utils/clusterHeatmap';

import {
  Typography,
  Box,
  Stack,
  Divider,
  tokens,
  SquareMarker,
} from '@cast/design-system';

type LegendItemProps = {
  color: string;
  label: string;
};

const LegendItem = ({ color, label }: LegendItemProps) => (
  <Stack direction="row" alignItems="center" gap={4}>
    <Typography
      variant="P12R"
      color={tokens.theme.fg.structural.secondary}
      whiteSpace="nowrap"
    >
      {label}
    </Typography>
    <SquareMarker color={color} />
  </Stack>
);

const ScaleGradientLegend = ({
  endLabel,
  gradient,
  startLabel,
}: {
  endLabel: string;
  gradient: string;
  startLabel: string;
}) => (
  <Stack direction="row" alignItems="center" gap={8} flexWrap="nowrap">
    <Typography
      variant="L10B"
      color={tokens.theme.fg.structural.secondary}
      whiteSpace="nowrap"
    >
      {startLabel}
    </Typography>
    <Box
      sx={{
        width: 120,
        height: 8,
        borderRadius: '4px',
        background: gradient,
        flexShrink: 0,
      }}
    />
    <Typography
      variant="L10B"
      color={tokens.theme.fg.structural.secondary}
      whiteSpace="nowrap"
    >
      {endLabel}
    </Typography>
  </Stack>
);

type HeatmapLegendProps = {
  mode: HeatmapViewMode;
};

export const HeatmapLegend = ({ mode }: HeatmapLegendProps) => {
  if (mode === 'health') {
    const breachLow = computeSloColors('breach', 1).fill;
    const breachHigh = computeSloColors('breach', 7).fill;
    const riskLow = computeSloColors('risk', 1).fill;
    const riskHigh = computeSloColors('risk', 7).fill;
    const okColor = computeSloColors('ok', 1).fill;

    return (
      <Stack
        direction="row"
        alignItems="center"
        gap={12}
        flexWrap="wrap"
        data-testid="heatmap-legend"
      >
        <Stack direction="row" alignItems="center" gap={4} flexWrap="nowrap">
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
            whiteSpace="nowrap"
          >
            Unhealthy
          </Typography>
          <Box
            sx={{
              width: 64,
              height: 8,
              borderRadius: '4px',
              background: `linear-gradient(90deg, ${breachLow}, ${breachHigh})`,
              flexShrink: 0,
            }}
          />
        </Stack>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            borderColor: tokens.theme.border.structural.neutral,
            alignSelf: 'stretch',
            my: 0.5,
          }}
        />
        <Stack direction="row" alignItems="center" gap={4} flexWrap="nowrap">
          <Typography
            variant="P12R"
            color={tokens.theme.fg.structural.secondary}
            whiteSpace="nowrap"
          >
            At risk
          </Typography>
          <Box
            sx={{
              width: 64,
              height: 8,
              borderRadius: '4px',
              background: `linear-gradient(90deg, ${riskLow}, ${riskHigh})`,
              flexShrink: 0,
            }}
          />
        </Stack>
        <Divider
          orientation="vertical"
          flexItem
          sx={{
            borderColor: tokens.theme.border.structural.neutral,
            alignSelf: 'stretch',
            my: 0.5,
          }}
        />
        <LegendItem color={okColor} label="Healthy" />
      </Stack>
    );
  }

  const lowWaste = computeWasteColors(2).fill;
  const highWaste = computeWasteColors(77).fill;

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap={12}
      data-testid="heatmap-legend"
    >
      <Typography
        variant="P12R"
        color={tokens.theme.fg.structural.secondary}
        whiteSpace="nowrap"
      >
        Optimization potential
      </Typography>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          borderColor: tokens.theme.border.structural.neutral,
          alignSelf: 'stretch',
          my: 0.5,
        }}
      />
      <ScaleGradientLegend
        startLabel="HIGH"
        endLabel="LOW"
        gradient={`linear-gradient(90deg, ${highWaste}, ${lowWaste})`}
      />
    </Stack>
  );
};
